import React, { useContext, useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import { AuthContext } from "../context/AuthContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import ToolInfoModal from "../components/common/ToolInfoModal";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const ENDPOINT = "/product/sku-builder-accessories";

function SkuBuilderAccessoriesTool() {
  const [linesheetSource, setLinesheetSource] = useState("google");
  const [inputFile, setInputFile] = useState(null);
  const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
  const [sheetName, setSheetName] = useState("Accessories");
  const [rowsToProcess, setRowsToProcess] = useState("");
  const [includeOnMap, setIncludeOnMap] = useState(false);
  const [customTags, setCustomTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "skuBuilderAccessories.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "SKU Builder (Accessories)",
    description:
      "Build accessories / apparel / bags SKUs from the canonical Accessories Linesheet. Runs as a background job — you can leave this page while it runs and come back for the Matrixify-ready Product Sheet.",
    steps: [
      "Download the Accessories Linesheet template (link on the right). Header is on row 4. Tab name defaults to 'Accessories' but the builder accepts any tab — type 'Bags', 'Pedorthic', etc. for category-specific tabs.",
      "Fill it in — one row per parent product. Leave Size blank for single-variant products (creams, bags, drinkware).",
      "For sized items use 'S, M, L', '0-16' (with Size Increment Whole), '5-12,13' (Half), or explicit lists like '6 + 1/2, 6 + 5/8, 7'.",
      "Set RS = True on retail-sale rows. The builder will prefix the title with '(RS) ', the handle with 'rs-', and the variant SKU with 'RS-'. Use for Baggallini RS bags and any outlet-RS items.",
      "Upload the file and type the sheet (tab) name.",
      "Click 'Start Build'. When the job finishes, download the Product Sheet and import via Matrixify.",
    ],
    templates: [
      {
        name: "Accessories Linesheet Template",
        link: `${flaskApi.defaults.baseURL}/templates/download/template_accessories_linesheet.xlsx`,
      },
    ],
  };

  const guideTips = [
    "Output mirrors the shoe SKU Builder Product Sheet column-for-column, so Matrixify imports the same way.",
    "Blank Size → single-variant product with Option1 Name = 'Title' / Value = 'Default Title' (matches existing creams, drinkware, bags on Shopify).",
    "Sized accessories get Variant SKU '<parent_sku> <size>' with a literal space (Birkenstock socks '1010371 S', Darn Tough '1907-MOSS HEATHER M').",
    "Bags pattern: leave Gender tag blank, Size blank, set SKU = '<base_sku>-<color_code>' (e.g. 'AAC459-B0001'), RS = True. Title becomes '(RS) Baggallini …' and variant SKU becomes 'RS-AAC459-B0001'.",
    "Product Type must match an entry in the tax mapping (Accessories - Socks - Lifestyle, Accessories - Bags - Handbags / Crossbody / Backpacks / Wallets, Accessories - Shoe Care, etc.). The Avalara tax code column is auto-filled.",
    "Tags get assembled the same way as shoes: Vendor + Gender + color_* + Product Type pieces + Season + 'New' + 'lookbook' + Intro date tag. RS rows also get the 'rs' tag appended.",
    "Laces have their own dedicated tool (SKU Builder (Laces)) — Option1 is Length with the inch mark, length is kept out of the title. Don't use this tool for laces.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(
            `SKU build ${j.job_id} complete. Product Sheet is ready.`,
            "success"
          );
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `SKU build ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const hasLinesheet =
      linesheetSource === "google"
        ? Boolean(selectedGoogleSheet?.id)
        : Boolean(inputFile);

    if (!hasLinesheet) {
      addToast(
        linesheetSource === "google"
          ? "Please choose a shared Google Sheet."
          : "Please upload the accessories linesheet.",
        "error"
      );
      return;
    }

    const trimmedSheet = sheetName.trim();
    if (!trimmedSheet) {
      addToast("Please enter the sheet (tab) name.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (linesheetSource === "upload" && inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    if (linesheetSource === "google") {
      formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
      formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
    } else {
      formData.append("input_file", inputFile);
    }
    formData.append("sheet_name", trimmedSheet);
    if (rowsToProcess.trim()) {
      formData.append("rows_to_process", rowsToProcess.trim());
    }
    if (includeOnMap) {
      formData.append("include_on_map", "true");
    }
    if (customTags.trim()) {
      formData.append("custom_tags", customTags);
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (progressEvent) => {
          const pct = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(pct);
        },
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`SKU build ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();

      if (linesheetSource === "google" && selectedGoogleSheet?.id) {
        markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
      }
      if (linesheetSource === "upload") {
        setInputFile(null);
      }
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start SKU build.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename ||
          job.result_filename ||
          "sku_builder_accessories.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Product Sheet downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download Product Sheet.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="SKU Builder (Accessories)"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Builder Settings</h3>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Sheet (Tab) Name</label>
                <input
                  type="text"
                  className="tool-input"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  placeholder="Accessories"
                  required
                  disabled={uploading}
                />
                <p className="tool-helper-text">
                  Defaults to "Accessories" (the template's tab). Change if your file
                  uses a different tab name.
                </p>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Rows to Process (Optional)</label>
                <input
                  type="text"
                  className="tool-input"
                  value={rowsToProcess}
                  onChange={(event) => setRowsToProcess(event.target.value)}
                  placeholder="e.g. 6-10, 12, 14 (Leave empty for all)"
                  disabled={uploading}
                />
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Tag Options</label>
                <div className="shopify-checkbox-row">
                  <label className="shopify-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeOnMap}
                      onChange={(event) => setIncludeOnMap(event.target.checked)}
                      disabled={uploading}
                    />
                    Add on-map tag
                  </label>
                </div>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Custom Tags (Optional)</label>
                <textarea
                  className="tool-input"
                  value={customTags}
                  onChange={(event) => setCustomTags(event.target.value)}
                  placeholder="e.g. preorder, staff-pick"
                  disabled={uploading}
                />
                <p className="tool-helper-text">
                  Comma- or line-separated tags added to every generated row.
                </p>
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Start Build"}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading linesheet…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Source</h3>
              </div>
              <LinesheetSourceSelector
                source={linesheetSource}
                onSourceChange={setLinesheetSource}
                uploadFile={inputFile}
                onUploadFileChange={setInputFile}
                selectedGoogleSheet={selectedGoogleSheet}
                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                uploadLabel="Upload Accessories Linesheet (Max 100MB)"
              />
            </section>
          </div>
        </form>

        <ToolJobsSection
          title="Your SKU Builds"
          emptyText="No builds yet. Upload a linesheet above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const variants = Number(progress.variants) || 0;
                const stage = progress.stage || "";
                const pct =
                  total > 0
                    ? Math.min(100, Math.round((processed / total) * 100))
                    : 0;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Sheet</span>
                      <span className="tool-job-value">{j.sheet_name || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                        {stage ? ` (${stage})` : ""}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </>
                    ) : null}
                    {variants > 0 ? (
                      <div className="tool-job-counts">
                        <span>Variants: {variants.toLocaleString()}</span>
                      </div>
                    ) : null}
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id
                          ? "Downloading…"
                          : "Download Product Sheet"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default SkuBuilderAccessoriesTool;
